import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
} from 'react';

import { cn } from '@/shared/lib/cn';

import { FieldLabel } from './FieldLabel';
import { Icon } from './Icon';

/** Tempo sem digitar antes de pedir a busca ao servidor. */
const ESPERA_DA_BUSCA_MS = 250;
/** Altura maior da lista, com umas seis opcoes de duas linhas. */
const ALTURA_DA_LISTA = 264;
/** Abaixo desta folga, a lista abre para cima, se la houver mais espaco. */
const FOLGA_MINIMA = 160;

export interface ComboboxOption {
  value: string;
  label: string;
  /** Segunda linha da opcao: o CNPJ e o nome fantasia, por exemplo. */
  description?: string | undefined;
}

export interface ComboboxProps {
  label: string;
  /** O valor escolhido; vazio e nada escolhido. */
  value: string;
  /** O texto da escolha, que pode estar fora do resultado da busca atual. */
  selectedLabel: string;
  options: ComboboxOption[];
  /** Recebe a opcao escolhida, ou nulo quando a pessoa limpa o campo. */
  onChange: (option: ComboboxOption | null) => void;
  /** Recebe o termo digitado, ja com a espera; vazio volta a lista inicial. */
  onSearch: (term: string) => void;
  loading?: boolean | undefined;
  required?: boolean | undefined;
  disabled?: boolean | undefined;
  /** Mostra o botao que limpa a escolha. Para o campo opcional. */
  clearable?: boolean | undefined;
  placeholder?: string | undefined;
  hint?: string | undefined;
  error?: string | undefined;
  /** Texto do icone de ajuda, como no campo de texto. */
  help?: string | false | undefined;
  emptyMessage?: string | undefined;
}

/**
 * Busca e selecao num campo so: ao clicar mostra as opcoes, ao digitar filtra, ao
 * escolher preenche.
 *
 * Quem filtra e o servidor: as listas de tomadores, corretores e segurados nao cabem
 * inteiras no navegador. A lista fica em posicao fixa porque o campo costuma estar
 * dentro do corpo rolavel de um modal, que cortaria uma lista absoluta; e fica dentro
 * do campo, e nao num portal, porque so o conteudo do dialogo nativo aparece acima do
 * fundo dele.
 *
 * Segue o padrao de combobox com lista da WAI-ARIA: o foco fica no campo, e a opcao
 * ativa e anunciada por aria-activedescendant. Setas movem, Enter escolhe, Esc fecha a
 * lista sem fechar o modal, e Tab sai.
 */
export function Combobox({
  label,
  value,
  selectedLabel,
  options,
  onChange,
  onSearch,
  loading,
  required,
  disabled,
  clearable,
  placeholder,
  hint,
  error,
  help,
  emptyMessage = 'Nenhuma opcao encontrada.',
}: ComboboxProps) {
  const inputId = useId();
  const listId = `${inputId}-lista`;
  const hintId = `${inputId}-hint`;
  const errorId = `${inputId}-error`;

  const campo = useRef<HTMLDivElement>(null);
  const lista = useRef<HTMLUListElement>(null);
  const espera = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [aberto, setAberto] = useState(false);
  // O que a pessoa esta digitando. Nulo mostra a escolha.
  const [texto, setTexto] = useState<string | null>(null);
  const [ativo, setAtivo] = useState(-1);
  const [posicao, setPosicao] = useState<CSSProperties>({});

  const cancelarEspera = useCallback(() => {
    if (espera.current !== null) clearTimeout(espera.current);
    espera.current = null;
  }, []);
  useEffect(() => cancelarEspera, [cancelarEspera]);

  const medir = useCallback(() => {
    const caixa = campo.current?.getBoundingClientRect();
    if (!caixa) return;
    const abaixo = window.innerHeight - caixa.bottom;
    const acima = caixa.top;
    const paraCima = abaixo < FOLGA_MINIMA && acima > abaixo;
    setPosicao({
      left: caixa.left,
      width: caixa.width,
      maxHeight: Math.min(ALTURA_DA_LISTA, (paraCima ? acima : abaixo) - 12),
      ...(paraCima
        ? { bottom: window.innerHeight - caixa.top + 4 }
        : { top: caixa.bottom + 4 }),
    });
  }, []);

  useLayoutEffect(() => {
    if (!aberto) return;
    medir();
    // A fase de captura pega a rolagem do corpo do modal, que nao sobe ate a janela.
    window.addEventListener('scroll', medir, true);
    window.addEventListener('resize', medir);
    return () => {
      window.removeEventListener('scroll', medir, true);
      window.removeEventListener('resize', medir);
    };
  }, [aberto, medir]);

  useEffect(() => {
    if (ativo < 0) return;
    lista.current?.children[ativo]?.scrollIntoView?.({ block: 'nearest' });
  }, [ativo]);

  function fechar() {
    cancelarEspera();
    setAberto(false);
    setAtivo(-1);
    // Quem digitou e nao escolheu volta para a escolha e para a lista inicial.
    if (texto !== null) {
      setTexto(null);
      onSearch('');
    }
  }

  function escolher(opcao: ComboboxOption) {
    fechar();
    onChange(opcao);
  }

  function aoDigitar(termo: string) {
    setTexto(termo);
    setAberto(true);
    setAtivo(-1);
    cancelarEspera();
    espera.current = setTimeout(() => onSearch(termo.trim()), ESPERA_DA_BUSCA_MS);
  }

  function aoTeclar(event: KeyboardEvent<HTMLInputElement>) {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!aberto) setAberto(true);
        setAtivo((atual) => (options.length ? (atual + 1) % options.length : -1));
        return;
      case 'ArrowUp':
        event.preventDefault();
        if (!aberto) setAberto(true);
        setAtivo((atual) =>
          options.length ? (atual <= 0 ? options.length : atual) - 1 : -1,
        );
        return;
      case 'Enter': {
        if (!aberto) return;
        // Enter com a lista aberta escolhe; nunca envia o formulario.
        event.preventDefault();
        const opcao = options[ativo];
        if (opcao) escolher(opcao);
        return;
      }
      case 'Escape':
        if (!aberto) return;
        // Sem isto, o Esc fecharia o modal junto com a lista.
        event.preventDefault();
        event.stopPropagation();
        fechar();
        return;
      default:
    }
  }

  const invalido = Boolean(error);
  const describedBy = [hint && !error ? hintId : null, error ? errorId : null]
    .filter(Boolean)
    .join(' ');
  const opcaoAtiva = ativo >= 0 ? `${listId}-${ativo}` : undefined;
  const podeLimpar = Boolean(clearable && value && !disabled);

  return (
    <div className="flex flex-col gap-1.5">
      <FieldLabel
        htmlFor={inputId}
        label={label}
        className="text-xs font-semibold text-ink-700"
        help={help}
      />

      <div ref={campo} className="relative">
        <input
          id={inputId}
          type="text"
          role="combobox"
          autoComplete="off"
          spellCheck={false}
          aria-autocomplete="list"
          aria-expanded={aberto}
          aria-controls={listId}
          aria-activedescendant={aberto ? opcaoAtiva : undefined}
          aria-invalid={invalido ? true : undefined}
          aria-describedby={describedBy || undefined}
          required={required}
          disabled={disabled}
          placeholder={placeholder}
          value={texto ?? selectedLabel}
          className={cn(
            'h-10 w-full rounded-control border bg-surface px-3 text-sm text-ink-900 shadow-(--shadow-xs)',
            'placeholder:text-ink-400',
            'transition-[border-color,box-shadow] duration-(--duration-fast)',
            'focus:outline-none focus:ring-4',
            podeLimpar ? 'pr-9' : null,
            invalido
              ? 'border-danger-500 focus:border-danger-500 focus:ring-danger-500/15'
              : 'border-line-strong hover:border-ink-400 focus:border-brand-500 focus:ring-brand-500/15',
          )}
          onClick={(event) => {
            // Selecionar o texto faz a primeira tecla trocar a escolha pela busca.
            event.currentTarget.select();
            setAberto(true);
          }}
          onChange={(event) => aoDigitar(event.target.value)}
          onKeyDown={aoTeclar}
          onBlur={fechar}
        />

        {podeLimpar ? (
          <button
            type="button"
            aria-label={`Limpar ${label}`}
            className="absolute inset-y-0 right-2 my-auto flex size-6 items-center justify-center rounded text-ink-400 hover:text-ink-700 focus:outline-none focus:ring-3 focus:ring-brand-500/15"
            onClick={() => {
              fechar();
              onChange(null);
            }}
          >
            <Icon name="close" size={14} />
          </button>
        ) : null}

        {aberto ? (
          // O clique na lista nao tira o foco do campo: sem isto, o blur fecharia a
          // lista antes de o clique chegar a opcao.
          <div
            className="fixed z-50 flex flex-col overflow-hidden rounded-control border border-line bg-surface shadow-(--shadow-lg)"
            style={posicao}
            onMouseDown={(event) => event.preventDefault()}
          >
            <ul
              ref={lista}
              id={listId}
              role="listbox"
              aria-label={label}
              aria-busy={loading ? true : undefined}
              className="min-h-0 flex-1 overflow-y-auto py-1"
            >
              {options.map((opcao, indice) => (
                <li
                  key={opcao.value}
                  id={`${listId}-${indice}`}
                  role="option"
                  aria-selected={opcao.value === value}
                  className={cn(
                    'flex cursor-pointer items-center justify-between gap-2 px-3 py-1.5 text-sm',
                    indice === ativo ? 'bg-brand-50' : null,
                  )}
                  onMouseEnter={() => setAtivo(indice)}
                  onClick={() => escolher(opcao)}
                >
                  <span className="min-w-0">
                    <span className="block truncate text-ink-900">{opcao.label}</span>
                    {opcao.description ? (
                      <span className="block truncate text-2xs text-ink-500">
                        {opcao.description}
                      </span>
                    ) : null}
                  </span>
                  {opcao.value === value ? (
                    <Icon name="check" size={14} className="shrink-0 text-brand-700" />
                  ) : null}
                </li>
              ))}
            </ul>
            {options.length === 0 ? (
              <p className="px-3 py-2 text-xs text-ink-500">
                {loading ? 'Buscando...' : emptyMessage}
              </p>
            ) : null}
            <p role="status" className="sr-only">
              {loading
                ? 'Buscando.'
                : options.length === 1
                  ? '1 opcao.'
                  : `${options.length} opcoes.`}
            </p>
          </div>
        ) : null}
      </div>

      {hint && !error ? (
        <p id={hintId} className="text-2xs text-ink-500">
          {hint}
        </p>
      ) : null}

      {error ? (
        <p id={errorId} className="text-2xs font-medium text-danger-700">
          {error}
        </p>
      ) : null}
    </div>
  );
}
